import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { normalizeCalendarMonth } from './calendarMonth';
import { fetchFeedbackFeedbacks } from './feedbackSheetApi';

const LIVE_CLASS_SHEET_ID = process.env.REACT_APP_SHEET_ID || '1_ncrpawhwAdibJIPYaloRhN-JWRL3duU2IbO8hDJL1M';
const PROMO_SHEET_ID = '1y4AQFJqlLvTK0aJ_00Jm7Xh6R98K20m5Z-UHh9WINFA';

const LIVE_CLASS_URL = `https://docs.google.com/spreadsheets/d/${LIVE_CLASS_SHEET_ID}/gviz/tq?tqx=out:json&sheet=Dashboard&range=A1:Z50000`;
const PROMO_URL = `https://docs.google.com/spreadsheets/d/${PROMO_SHEET_ID}/gviz/tq?tqx=out:json&sheet=Promotional%20Class%20Routine%20Dashboard&range=A1:R50000`;
const CORS_PROXY = 'https://cors-anywhere.herokuapp.com/';

export function parseSheetDate(str) {
  if (!str) return '';
  const match = str.match(/^Date\((\d+),(\d+),(\d+)\)$/);
  if (match) {
    const [, year, month, day] = match;
    const monthNum = parseInt(month) + 1;
    return `${year}-${monthNum.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  }
  return str;
}

/** Google Sheets TRUE / checkbox / "TRUE" string → boolean */
function isSheetTrue(val) {
  if (val === true || val === 1) return true;
  if (typeof val === 'string') {
    const s = val.trim().toUpperCase();
    return s === 'TRUE' || s === '1' || s === 'YES';
  }
  return false;
}

function parseSheetData(raw) {
  const json = JSON.parse(raw.substring(47).slice(0, -2));
  const cols = json.table.cols.map(col => col.label.trim());
  const rows = json.table.rows.map(row =>
    Object.fromEntries(row.c.map((cell, i) => [cols[i], cell ? cell.v : '']))
  );
  return { cols, rows };
}

async function fetchFromSheet(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.text();
}

const DataContext = createContext(null);

export function DataProvider({ children }) {
  const [liveClassData, setLiveClassData] = useState([]);
  const [liveClassLoading, setLiveClassLoading] = useState(true);
  const [liveClassError, setLiveClassError] = useState(null);

  const [promoData, setPromoData] = useState([]);
  const [promoLoading, setPromoLoading] = useState(true);
  const [promoError, setPromoError] = useState(null);

  const fetchLiveClassData = useCallback(async () => {
    setLiveClassLoading(true);
    setLiveClassError(null);
    try {
      let raw;
      try {
        raw = await fetchFromSheet(LIVE_CLASS_URL);
      } catch {
        raw = await fetchFromSheet(CORS_PROXY + LIVE_CLASS_URL);
      }
      const { rows } = parseSheetData(raw);
      if (!rows || rows.length === 0) {
        setLiveClassError('No data found in the Google Sheet.');
        setLiveClassData([]);
        return;
      }
      const processed = rows.map((row, index) => ({
        id: index + 1,
        date: row['Class Date'] || '',
        dateISO: parseSheetDate(row['Class Date']),
        time: row['Time'] || '',
        class: row['Class'] || '',
        subject: row['Subject'] || '',
        chapter: row['Chapter'] || '',
        part: row['পর্ব/Part'] || '',
        topic: row['Topic'] || '',
        teacher: row['Teacher'] || '',
        thumbnail: row['Thumbnail'] || '',
        reminderPoster: row['Reminder Poster'] || '',
        projectName: row['Project Name'] || '',
        lateCount: isSheetTrue(row['Late Count']),
        routineTimeChange: isSheetTrue(
          row['Routine Timr Change'] ?? row['Routine Time Change']
        ),
      }));
      setLiveClassData(processed);
    } catch (err) {
      console.error('Live class fetch failed:', err);
      setLiveClassError('Failed to load live class schedule. Please check your connection.');
    } finally {
      setLiveClassLoading(false);
    }
  }, []);

  const fetchPromoData = useCallback(async () => {
    setPromoLoading(true);
    setPromoError(null);
    try {
      let raw;
      try {
        raw = await fetchFromSheet(PROMO_URL);
      } catch {
        raw = await fetchFromSheet(CORS_PROXY + PROMO_URL);
      }
      const { rows } = parseSheetData(raw);
      if (!rows || rows.length === 0) {
        setPromoError('No data found in the promotional sheet.');
        setPromoData([]);
        return;
      }
      const processed = rows
        .map((row, index) => ({
          id: index + 1,
          thumbnailLink: row['Thumbnail Link'] || '',
          shootDeadline: parseSheetDate(row['Shoot Deadline']),
          shootDeadlineRaw: row['Shoot Deadline'] || '',
          time: row['Time'] || '',
          class: row['Class'] || '',
          month: normalizeCalendarMonth(row['Month']) || '',
          caption: row['Caption'] || '',
          subject: row['Subject'] || '',
          subjectBn: row['Subject (বাংলা)'] || '',
          chapter: row['Chapter'] || '',
          part: row['পর্ব/Part'] || '',
          topic: row['Topic'] || '',
          topicList: row['Topic List'] || '',
          teacherName: row['Teacher Name'] || '',
          courseName: row['Course Name'] || '',
          shootStatus:
            row['Shoot Status'] != null && row['Shoot Status'] !== ''
              ? String(row['Shoot Status']).trim()
              : '',
          rawVideoLink: row['RAW Video Link'] || '',
          ytLink: row['YT Link'] || '',
        }))
        .filter(row => row.class || row.subject || row.teacherName || row.courseName);
      setPromoData(processed);
    } catch (err) {
      console.error('Promo fetch failed:', err);
      setPromoError('Failed to load promotional schedule. Please check your connection.');
    } finally {
      setPromoLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLiveClassData();
    fetchPromoData();
    // Warm feedback sheet in parallel so /feedback opens instantly after home.
    fetchFeedbackFeedbacks().catch(() => {});
  }, [fetchLiveClassData, fetchPromoData]);

  return (
    <DataContext.Provider value={{
      liveClassData,
      liveClassLoading,
      liveClassError,
      refetchLiveClass: fetchLiveClassData,
      promoData,
      promoLoading,
      promoError,
      refetchPromo: fetchPromoData,
    }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  return useContext(DataContext);
}
